import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, HueLogoTile, Input, PasswordStrengthInput } from '@hue/design-system';
import { ArrowLeft, CheckCircle2, KeyRound, Mail, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { getApiError, passwordApi } from '../lib/api.js';
import { ThemeToggle } from './theme-toggle.js';

const requestSchema = z.object({ email: z.email('Informe um e-mail válido.') });
const resetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Use ao menos 8 caracteres.')
      .regex(/[A-Z]/, 'Inclua ao menos uma letra maiúscula.')
      .regex(/\d/, 'Inclua ao menos um número.')
      .regex(/[^A-Za-z0-9]/, 'Inclua ao menos um caractere especial.'),
    confirmation: z.string(),
  })
  .refine((data) => data.password === data.confirmation, {
    path: ['confirmation'],
    message: 'As senhas precisam ser iguais.',
  });

type RequestForm = z.infer<typeof requestSchema>;
type ResetForm = z.infer<typeof resetSchema>;

export function PasswordRecovery({
  token,
  onBack,
  onUseDevelopmentToken,
}: {
  token: string | null;
  onBack: () => void;
  onUseDevelopmentToken: (token: string) => void;
}) {
  const [developmentToken, setDevelopmentToken] = useState<string>();
  const [requestAccepted, setRequestAccepted] = useState(false);
  const [resetCompleted, setResetCompleted] = useState(false);
  const [requestError, setRequestError] = useState<string>();
  const request = useForm<RequestForm>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  });
  const reset = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirmation: '' },
    mode: 'onChange',
  });

  async function submitRequest(data: RequestForm) {
    setRequestError(undefined);
    try {
      const result = await passwordApi.requestReset(data.email);
      setDevelopmentToken(result.developmentToken);
      setRequestAccepted(true);
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  async function submitReset(data: ResetForm) {
    if (!token) return;
    setRequestError(undefined);
    try {
      await passwordApi.completeReset({ token, password: data.password });
      setResetCompleted(true);
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  const heading = resetCompleted
    ? 'Senha redefinida'
    : token
      ? 'Crie uma nova senha'
      : 'Recupere seu acesso';

  return (
    <main className="relative grid min-h-screen place-items-center overflow-hidden bg-surface px-4 py-10">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-12 h-64 w-64 -translate-x-1/2 rounded-full bg-brand-500/[.07] blur-3xl" />
      </div>
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-md">
        <div className="mb-7 text-center">
          <HueLogoTile className="mx-auto mb-4 size-16 rounded-2xl" />
          <h1 className="text-2xl font-bold tracking-[-.035em]">
            HUE<span className="text-brand-400">•</span>System
          </h1>
          <p className="mt-1.5 text-sm text-content-muted">Segurança da identidade</p>
        </div>

        <Card className="border-surface-border bg-surface-card p-6 sm:p-7">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">{heading}</h2>
              <p className="mt-1.5 text-sm leading-5 text-content-muted">
                {resetCompleted
                  ? 'Seu novo acesso está pronto e as sessões anteriores foram encerradas.'
                  : token
                    ? 'A nova senha será usada em todos os seus acessos.'
                    : 'Mandamos o link por e-mail se o endereço estiver vinculado a uma conta ativa.'}
              </p>
            </div>
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
              {resetCompleted ? (
                <CheckCircle2 className="size-5" />
              ) : (
                <ShieldCheck className="size-5" />
              )}
            </span>
          </div>

          {requestError ? (
            <div
              role="alert"
              className="mb-5 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
            >
              {requestError}
            </div>
          ) : null}

          {resetCompleted ? (
            <Button className="w-full" size="lg" onClick={onBack}>
              Voltar para entrar
            </Button>
          ) : token ? (
            <form
              noValidate
              className="space-y-4"
              onSubmit={(event) => void reset.handleSubmit(submitReset)(event)}
            >
              <Controller
                control={reset.control}
                name="password"
                render={({ field, fieldState }) => (
                  <PasswordStrengthInput
                    label="Nova senha"
                    value={field.value}
                    onValueChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    error={fieldState.error?.message}
                    disabled={reset.formState.isSubmitting}
                  />
                )}
              />
              <Input
                label="Confirme a nova senha"
                type="password"
                autoComplete="new-password"
                leftIcon={<KeyRound className="size-4" />}
                error={reset.formState.errors.confirmation?.message}
                {...reset.register('confirmation')}
              />
              <Button className="w-full" size="lg" loading={reset.formState.isSubmitting}>
                Redefinir senha
              </Button>
            </form>
          ) : requestAccepted ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-positive/30 bg-positive/10 px-4 py-4 text-sm leading-6 text-content-secondary">
                Se houver uma conta ativa para esse e-mail, o link de recuperação já está a caminho.
                Ele vale por 15 minutos.
              </div>
              {developmentToken ? (
                <button
                  type="button"
                  onClick={() => onUseDevelopmentToken(developmentToken)}
                  className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-brand-500/35 bg-brand-500/10 px-4 py-3 text-sm font-semibold text-brand-400 transition hover:border-brand-500/60 hover:bg-brand-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
                >
                  <KeyRound className="size-4" />
                  Testar redefinição local
                </button>
              ) : null}
            </div>
          ) : (
            <form
              noValidate
              className="space-y-5"
              onSubmit={(event) => void request.handleSubmit(submitRequest)(event)}
            >
              <Input
                label="E-mail da conta"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="voce@exemplo.com"
                leftIcon={<Mail className="size-4" />}
                error={request.formState.errors.email?.message}
                {...request.register('email')}
              />
              <Button className="w-full" size="lg" loading={request.formState.isSubmitting}>
                Enviar link de recuperação
              </Button>
            </form>
          )}

          {!resetCompleted ? (
            <button
              type="button"
              onClick={onBack}
              className="mt-5 flex min-h-10 w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm text-content-secondary transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            >
              <ArrowLeft className="size-4" />
              Voltar para entrar
            </button>
          ) : null}
        </Card>
      </div>
    </main>
  );
}
